<template>
  <div id="account-change-user" tabindex="-1">
    <h1 class="title">{{ $t('general.your-data') }}</h1>
    <div class="box">
      <ValidationObserver v-slot="{invalid}" v-if="userdata">
        <b-notification type="is-success" v-if="saved" :closable="false" role="alert" aria-live="assertive">
          {{ $t('notices.data-has-been-refreshed') }}
        </b-notification>
        <b-notification type="is-danger" v-if="error" :closable="false" role="alert" aria-live="assertive">
          <span v-if="errors.length == 0">{{ $t('errors.retryable-error') }}</span>
          <ul v-if="errors.length > 0">
            <li v-for="(e, i) in registerErrors" :key="i">{{ e.value }}</li>
          </ul>
        </b-notification>
        <h2 class="title is-size-5">{{ $t('general.contact-data') }}</h2>
        <div class="block">
          <ValidationProvider :name="`${$t('general.male')}/${$t('general.female')}`" rules="oneOf:10,20" v-slot="{errors}">
            <fieldset aria-describedby="error-sex">
              <legend class="is-sr-only">{{ $t('general.salutation') }}</legend>
              <b-radio id="radioMale" v-model="userdata.sex" name="sex" native-value="10">
                <span class="is-sr-only">{{ $t('general.salutation') }}</span>
                {{ $t('general.male') }}</b-radio
              >
              <b-radio id="radioFemale" v-model="userdata.sex" name="sex" native-value="20">
                <span class="is-sr-only">{{ $t('general.salutation') }}</span>
                {{ $t('general.female') }}</b-radio
              >
              <p id="error-sex" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
            </fieldset>
          </ValidationProvider>
        </div>
        <div class="columns is-multiline">
          <div class="column is-12-tablet is-5-widescreen">
            <ValidationProvider name="Vorname" :rules="{required: true}" v-slot="{errors}">
              <b-field expanded label-position="inside" label-for="account-firstName">
                <template #label>
                  {{ $t('general.firstname') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-firstName" aria-describedby="error-firstName" name="firstName" v-model="userdata.firstName"></b-input>
                <p id="error-firstName" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-12-tablet is-7-widescreen">
            <ValidationProvider name="Nachname" :rules="{required: true}" v-slot="{errors}">
              <b-field label-for="account-lastName" expanded label-position="inside">
                <template #label>
                  {{ $t('general.lastname') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-lastName" aria-describedby="error-lastName" name="lastName" v-model="userdata.lastName"></b-input>
                <p id="error-lastName" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
        </div>
        <Separator />
        <h2 class="title is-size-5">{{ $t('general.phone-email-data') }}</h2>
        <div class="columns is-multiline">
          <div class="column is-12-tablet is-5-widescreen">
            <b-field :label="$t('general.phone')" label-for="account-phone" expanded label-position="inside">
              <b-input id="account-phone" v-model="userdata.phoneNumber" inputmode="tel"></b-input>
            </b-field>
          </div>
          <div class="column is-12-tablet is-7-widescreen">
            <ValidationProvider :name="$t('general.email')" :rules="{required: true, email: true}" v-slot="{errors}">
              <b-field label-for="account-email" expanded label-position="inside">
                <template #label>
                  {{ $t('general.email') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-email" aria-describedby="error-email" name="email" v-model="userdata.email" inputmode="email"></b-input>
                <p id="error-email" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
        </div>
        <Separator />
        <h2 class="title is-size-5">{{ $t('general.address-data') }}</h2>
        <div class="columns is-multiline">
          <div class="column is-7 is-12-desktop is-7-widescreen">
            <ValidationProvider name="Adresse" :rules="{required: true}" v-slot="{errors}">
              <b-field label-for="account-street" label-position="inside">
                <template #label>
                  {{ $t('general.street') }} & {{ $t('general.number') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-street" aria-describedby="error-street" name="streetInput" v-model="userdata.address"></b-input>
                <p id="error-street" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-5 is-12-desktop is-5-widescreen">
            <ValidationProvider name="Land" :rules="{required: true}" v-slot="{errors}">
              <b-field label-for="account-country" label-position="inside">
                <template #label>
                  {{ $t('general.country') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-select id="account-country" aria-describedby="error-country" placeholder="Land" v-model="userdata.country" icon="globe" expanded>
                  <option v-for="option in isoCountries" :value="option.iso" :key="option.iso">{{ option.country }}</option>
                </b-select>
                <p id="error-country" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-4">
            <ValidationProvider name="PLZ" :rules="{required: true, numeric: true}" v-slot="{errors}">
              <b-field label-for="account-zip" label-position="inside">
                <template #label>
                  {{ $t('general.zip') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-zip" aria-describedby="error-zip" name="plz" v-model="userdata.zip" inputmode="numeric"></b-input>
                <p id="error-zip" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
          <div class="column is-8">
            <ValidationProvider name="Stadt" :rules="{required: true}" v-slot="{errors}">
              <b-field label-for="account-city" label-position="inside">
                <template #label>
                  {{ $t('general.city') }}<span aria-hidden="true">*</span> <span class="is-sr-only">{{ $t('general.required-field') }}</span>
                </template>
                <b-input id="account-city" aria-describedby="error-city" name="city" v-model="userdata.city"></b-input>
                <p id="error-city" class="help is-danger" v-for="error in errors" :key="error" role="alert" aria-live="assertive">{{ error }}</p>
              </b-field>
            </ValidationProvider>
          </div>
        </div>
        <p class="mb-4" aria-hidden="true">
          <small>(* {{ $t('general.required-field') }})</small>
        </p>
        <div class="is-sticky-mobile--bottom u-has-background-white-o75  u-p1--mobile">
          <b-button type="is-primary" size="is-medium" class="is-fullwidth--desktop" :disabled="invalid" @click="change(userdata)" expanded>{{ $t('general.daten-aktualisieren') }}</b-button>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Separator from '@/components/Separator'

export default {
  name: 'UserChangeData',
  components: {
    Separator,
  },
  computed: {
    ...mapGetters({
      userdata: '$_account/userdata',
      isoCountries: 'isoCountries',
    }),
  },
  data() {
    return {
      saved: false,
      error: false,
      errors: [],
    }
  },
  async mounted() {
    await this.$store.dispatch('$_account/getUserData')
  },
  methods: {
    async change(data) {
      this.saved = false
      this.errors = []
      this.error = false
      const res = await this.$store.dispatch('$_account/updateUserData', data)
      if (!res.success) {
        this.error = true
      } else {
        this.saved = true
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
